/**
 * 「日本語から翻訳する」ボタン (Amazon Translate 連携) 用の軽量コンテキスト
 * (Issue #492 / SBI-11)。
 *
 * ## なぜ props ではなくコンテキストなのか
 *
 * 翻訳ボタンを置くのは多言語入力欄 (`LocalizedTextFields`) の上部だが、この部品は
 *
 *   BroadcastPointsPage → BroadcastPointForm → BroadcastContentsEditor
 *     → ContentPropertyFields → 各 Fields (5 箇所) → LocalizedTextFields
 *
 * という 6 階層下にあり、5 つの Fields すべてを経由する。ハンドラを props で
 * バケツリレーすると中間 4 コンポーネントに「使わないのに通すだけ」の props が
 * 増え、1 箇所渡し忘れると **その入力欄だけ翻訳ボタンが出ない** という
 * 気づきにくい欠落になる。既に同種の課題を解いている `HomeNavigationGuard` と
 * 同じ方式 (軽量コンテキスト) に揃える。
 *
 * Provider が無い環境 (単体テストで個別コンポーネントを描画した場合など) でも
 * 動くよう、デフォルト値は `translate: null` (= 翻訳機能を無効化 / ボタン非表示)
 * とする。
 *
 * ## ⚠️ 翻訳結果は保存しない (誤案内防止)
 *
 * 本コンテキストが提供するのは「訳文を取得する」処理だけで、保存は行わない。
 * 呼び出し側は結果を入力欄の **下書き** として反映するに留め、保存は運用者の
 * 明示操作 (人手修正 → 保存ボタン) を必須とする。詳細は
 * `lambda/control-panel-api/index.py` の `translate_contents` を参照。
 */
import { createContext, useContext, useMemo } from 'react';

/** 1 言語ぶんの翻訳失敗情報 (`POST /translate` の `failed[]` と同形)。 */
export interface TranslateFailureInfo {
  /** 翻訳に失敗した language_key */
  language: string;
  /**
   * 失敗理由。`UnsupportedLanguage` (Amazon Translate 非対応言語) または
   * Amazon Translate の例外名 (`ThrottlingException` 等)。
   */
  reason: string;
}

/** 翻訳結果 (成功ぶんと失敗ぶんの両方を含む)。 */
export interface TranslateResult {
  /** language_key → 訳文。**成功した言語のみ** */
  translations: Record<string, string>;
  /** 翻訳できなかった言語と理由 (成功のみなら空配列) */
  failed: TranslateFailureInfo[];
}

/**
 * 日本語原文を各言語へ翻訳する処理。
 *
 * `null` のときは翻訳機能が利用できない (ボタンを表示しない)。
 */
export type TranslateHandler = (input: {
  /** 翻訳したい日本語の原文 */
  sourceText: string;
  /** 翻訳先の language_key 配列 */
  targetLanguages: readonly string[];
}) => Promise<TranslateResult>;

export interface TranslateContextValue {
  /** 翻訳処理 (未提供時は `null` = 機能無効) */
  translate: TranslateHandler | null;
}

const TranslateContext = createContext<TranslateContextValue>({ translate: null });

export function TranslateProvider({
  translate,
  children,
}: {
  translate: TranslateHandler | null;
  children: React.ReactNode;
}): React.JSX.Element {
  // `translate` は親で毎レンダリング再生成され得るため、value をメモ化しても
  // 参照は変わる。ここでのメモ化は「オブジェクトの無駄な再生成を避ける」目的に留める
  // (翻訳ボタンは再レンダリングが軽いので実用上の問題はない)。
  const value = useMemo<TranslateContextValue>(() => ({ translate }), [translate]);
  return <TranslateContext.Provider value={value}>{children}</TranslateContext.Provider>;
}

/** 多言語入力欄などから翻訳処理を取得する。 */
export function useTranslate(): TranslateContextValue {
  return useContext(TranslateContext);
}
